/* cspell:words Lixi */
import {
  CampaignMember,
  CampaignMemberStatus,
  useApproveManyMembersMutation,
  useSetApprovedMemberMutation,
  useUpdateMemberClaimedMutation,
} from '@app/services';
import { CopyIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  Flex,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AddReceiverModal,
  MembersTable,
  NotchedButton,
  StatusBadge,
} from '@components';
import { JsonRpcSigner } from '@ethersproject/providers';
import {
  Event,
  getCampaignById,
  selectCurrentLixiBagAddress,
  selectEventName,
  setEvent,
  setIdle,
} from '@features';
import { useAppDispatch, useAppSelector } from '@hooks';
import {
  fromWei,
  getMemberStatusByLabel,
  getMemberStatusProps,
  isHex,
  isMetamaskError,
} from '@utils';
import { useChainManager } from '../../chain/hooks/useChainManager';
import { useWhitelistEvents } from '../../chain/hooks/useWhitelistEvents';
import { sendGasToManyViaChainManager } from '../../chain/utils/sendGasUtils';
import { formatAddress } from '../../web3/utils/formatAddress';
import copyToClipboard from 'copy-to-clipboard';
import { round, omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Column } from 'react-table';
import {
  NumberRangeColumnFilter,
  SelectColumnFilter,
  SelectEmailFilter,
} from './_filterComponents';
import { ExportToCsv } from 'export-to-csv';
import moment from 'moment';

export interface MembersTableContainerProps {
  isOwner?: boolean;
  campaignId: string;
  isAdmin?: boolean;
  signer: JsonRpcSigner | undefined;
}

export const MembersTableContainer: React.FC<MembersTableContainerProps> = ({
  isOwner,
  campaignId,
  isAdmin,
  signer,
}) => {
  type MemberRow = {
    cbUsers: React.ReactNode;
    status: string;
    receiver?: string;
    email?: string;
    walletAddress?: string;
    balance: number;
    actions: React.ReactNode;
  };
  const [isSelectAllUsers, setIsSelectAllUsers] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  // Loading states cho các action để tránh click nhiều lần
  const [isRemovingWhitelister, setIsRemovingWhitelister] = useState<
    string | null
  >(null);
  const [isClaimingLixi, setIsClaimingLixi] = useState<string | null>(null);
  const [isSendingGas, setIsSendingGas] = useState<boolean>(false);
  const [isApprovingMany, setIsApprovingMany] = useState<boolean>(false);

  const campaign = useAppSelector(state => getCampaignById(state, campaignId));

  const [updateMemberClaimed] = useUpdateMemberClaimedMutation();
  const [approveManyUsers] = useApproveManyMembersMutation();
  const [setApprovedMember] = useSetApprovedMemberMutation();

  const dispatch = useAppDispatch();
  const eventName = useAppSelector(selectEventName);
  const lixiBagAddressFromSelector = useAppSelector(
    selectCurrentLixiBagAddress,
  );
  const lixiBagAddress = campaign?.lixiBagAddress || lixiBagAddressFromSelector;

  useWhitelistEvents(lixiBagAddress);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast({
    duration: 5000,
    isClosable: true,
    position: 'bottom',
  });

  const chainManager = useChainManager();

  const exportCsv = async () => {
    setIsExporting(true);
    const campaignName = campaign?.name;
    const list = campaign?.members;
    const exportData = list?.map(item => {
      const convertedDate = moment(item.createdAt).format(
        'YYYY-MM-DD HH:mm:ss',
      );
      const newObj = omit(item, ['user', 'createdAt']);
      const userObj = omit(item.user, [
        '_id',
        'avatar',
        'isEmailVerified',
        'balanceInWei',
      ]);
      return { ...newObj, ...userObj, convertedDate };
    });
    const options = {
      filename: campaignName?.split(' ').join('_'),
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: false,
      showTitle: true,
      title: campaignName,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    await csvExporter.generateCsv(exportData);
    setIsExporting(false);
  };

  const handleSelectAllUsers = (isTrue: boolean) => {
    if (isTrue) {
      const allPendingUsers = campaign?.members || ([] as CampaignMember[]);
      const allPendingUserIds = allPendingUsers.map(mb => mb.userId);
      setSelectedUserIds(allPendingUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  const getWalletAddresses = () => {
    if (!campaign || !campaign.members) {
      return [];
    }

    const selectedMembers =
      campaign.members.filter(mb => selectedUserIds.includes(mb.userId)) || [];
    return selectedMembers.map(mb => mb.user.walletAddress);
  };

  const handleAddWhitelister = async (
    userId: string,
    groupId?: number,
    userAddress?: string,
  ) => {
    // Call Lixibag sm
    try {
      dispatch(
        setEvent({
          eventName: isAdmin
            ? Event.SETWHITELISTER_BY_ADMIN_WAITING
            : Event.SETWHITELISTER_WAITING,
          payload: { campaignId, userId },
        }),
      );

      console.log(`Call addWhitelister with arguments`);
      console.log({
        groupId,
        userAddress,
        signer,
        isAdmin,
        blockhashHash: campaign?.blockhashHash,
        creatorWalletAddress: campaign?.creatorWalletAddress,
      });
      if (!groupId || !userAddress) {
        throw new Error('Missing required parameters');
      }
      const blockhashHash = campaign?.blockhashHash;
      const buyer = campaign?.creatorWalletAddress;

      if (!blockhashHash || !buyer) {
        throw new Error(
          'Thiếu blockhashHash hoặc buyer cho chiến dịch buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
        );
      }

      const result = await chainManager.addWhitelister({
        group: {
          groupId: groupId.toString(),
          blockhashHash,
          buyer,
        },
        user: userAddress,
      });

      // Kiểm tra kết quả transaction
      if (result.success) {
        let isSyncedWithBackend = false;
        try {
          await setApprovedMember({
            campaignId,
            userAddress,
            isWhitelisted: true,
          }).unwrap();
          isSyncedWithBackend = true;
        } catch (apiError) {
          console.error(
            'Failed to sync addWhitelister result to backend',
            apiError,
          );
          toast({
            title: 'Cập nhật trạng thái thất bại',
            description:
              apiError instanceof Error
                ? apiError.message
                : 'Không thể cập nhật trạng thái whitelister trên hệ thống. Vui lòng thử lại.',
            status: 'error',
          });
        }

        if (isSyncedWithBackend) {
          toast({
            title: 'Duyệt thành công',
            description: 'Bạn đã duyệt thành công cho người nhận này',
            status: 'success',
          });
        }
        // Reset state sau khi thành công
        dispatch(setIdle());
      } else {
        // Nếu transaction không thành công
        const errorMessage =
          result.error || 'Giao dịch không thành công. Vui lòng thử lại.';
        dispatch(setIdle());
        toast({
          title: 'Thất bại',
          description: errorMessage,
          status: 'error',
        });
      }
    } catch (err) {
      dispatch(setIdle());
      console.log(err);

      let message = 'Kết nối tới ví của bạn bị lỗi. Làm ơn thử lại.';

      if (err instanceof Error) {
        message = err.message;
      }

      if (isMetamaskError(err)) {
        message = err.data.message;
      }

      toast({
        title: 'Error',
        description: message,
        status: 'error',
      });
    }
  };

  const handleRemoveWhitelister = async (
    groupId?: number,
    userAddress?: string,
  ) => {
    if (!userAddress) {
      return;
    }

    setIsRemovingWhitelister(userAddress);
    try {
      if (!groupId || !userAddress) {
        throw new Error('Missing required parameters');
      }
      const blockhashHash = campaign?.blockhashHash;
      const buyer = campaign?.creatorWalletAddress;

      if (!blockhashHash || !buyer) {
        throw new Error(
          'Thiếu blockhashHash hoặc buyer cho chiến dịch buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
        );
      }

      await chainManager.removeWhitelister({
        group: {
          groupId: groupId.toString(),
          blockhashHash,
          buyer,
        },
        user: userAddress,
      });
      await setApprovedMember({
        campaignId,
        userAddress,
        isWhitelisted: false,
      }).unwrap();
      toast({
        title: 'Bạn đã huỷ thành công',
        status: 'success',
      });
    } catch (err) {
      console.log(err);
      toast({
        title: 'Thông báo lỗi',
        description: 'Thao tác huỷ lỗi. Bạn thử lại sau.',
        status: 'error',
      });
    } finally {
      setIsRemovingWhitelister(null);
    }
  };

  const handleClaimLixiByOwner = async (
    userId: string,
    groupId?: number,
    userAddress?: string,
  ) => {
    setIsClaimingLixi(userId);
    try {
      if (!groupId) {
        throw new Error('Missing groupId');
      }
      const blockhashHash = campaign?.blockhashHash;
      const buyer = campaign?.creatorWalletAddress;

      if (!blockhashHash || !buyer) {
        throw new Error(
          'Thiếu blockhashHash hoặc buyer cho chiến dịch buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
        );
      }

      const groupIdentifier = {
        groupId: groupId.toString(),
        blockhashHash,
        buyer,
      };

      if (!userAddress) {
        throw new Error('Missing receiver wallet address');
      }

      if (chainManager.claimLixiByOwner) {
        await chainManager.claimLixiByOwner({
          group: groupIdentifier,
          receiver: userAddress,
        });
      } else {
        await chainManager.claimLixi({
          group: groupIdentifier,
        });
      }

      await updateMemberClaimed({ campaignId, userId }).unwrap();

      toast({
        title: `Bạn đã gửi thành công lì xì tới ${userAddress}`,
        status: 'success',
      });
    } catch (err) {
      toast({
        title: 'Gửi lì xì thất bạn. Vui lòng thử lại sau',
        status: 'error',
      });
    } finally {
      setIsClaimingLixi(null);
      dispatch(setIdle());
    }
  };

  const ownerActions = (
    status: string,
    userId: string,
    groupId: number | undefined,
    userAddress: string | undefined,
  ): React.ReactElement | null => {
    if (status === 'PENDING') {
      const isLoadingAddWhitelister =
        eventName === Event.SETWHITELISTER_BY_ADMIN_WAITING ||
        eventName === Event.SETWHITELISTER_WAITING;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - TypeScript union type complexity issue with Chakra UI Flex
      return (
        <Flex>
          <NotchedButton
            variant="outline"
            color="primary"
            borderColor="primary"
            isLoading={isLoadingAddWhitelister}
            onClick={() => handleAddWhitelister(userId, groupId, userAddress)}
          >
            Duyệt
          </NotchedButton>
        </Flex>
      );
    } else if (status === 'APPROVED') {
      const isRemovingThisUser = Boolean(
        userAddress && isRemovingWhitelister === userAddress,
      );
      const isClaimingThisUser = isClaimingLixi === userId;

      return (
        <Flex gap={2} alignItems="center">
          <Flex alignItems="center" gap={1}>
            <Tooltip label="Bạn có thể huỷ yêu cầu nhận của người này">
              <InfoOutlineIcon />
            </Tooltip>
            <Text
              onClick={() => {
                if (!isRemovingThisUser && userAddress) {
                  handleRemoveWhitelister(groupId, userAddress);
                }
              }}
              css={{ 
                cursor: isRemovingThisUser ? 'not-allowed' : 'pointer',
                opacity: isRemovingThisUser ? 0.5 : 1,
              }}
            >
              {isRemovingThisUser ? 'Đang huỷ...' : 'Huỷ'}
            </Text>
          </Flex>
          <NotchedButton
            onClick={() => handleClaimLixiByOwner(userId, groupId, userAddress)}
            bgColor="primary"
            color="white"
            isLoading={isClaimingThisUser}
            disabled={isClaimingThisUser || isRemovingThisUser}
          >
            Gửi lì xì
          </NotchedButton>
        </Flex>
      );
    }
    return null;
  };

  const updateSelectedWallets = (selectedUserId: string) => {
    const idxElem = selectedUserIds.indexOf(selectedUserId);

    if (idxElem > -1) {
      setSelectedUserIds(
        selectedUserIds.filter(item => item !== selectedUserId),
      );
    } else {
      setSelectedUserIds([...selectedUserIds, selectedUserId]);
    }
  };

  const getInfoByStatus = (status: string) => {
    if (status === 'PENDING') {
      return 'Bạn cần duyệt yêu cầu này để chuyển lì xì cho người nhận';
    } else if (status === 'APPROVED') {
      return 'Nguời này đã được nhận thông báo để quay lại nhận lì xì';
    } else if (status === 'DECLINED') {
      return 'Người này đã bị từ chối gửi lì xì';
    } else if (status === 'CLAIMED') {
      return 'Người này đã nhận được lì xì';
    }
  };

  const handleSendGasToMany = async () => {
    const receivers = getWalletAddresses().filter(
      (addr): addr is string => !!addr && isHex(addr),
    );

    if (!receivers.length || !lixiBagAddress) {
      toast({
        title: 'Không có ví hợp lệ để tặng gas',
        status: 'warning',
      });
      return;
    }

    setIsSendingGas(true);
    try {
      await sendGasToManyViaChainManager(receivers, lixiBagAddress);
      toast({
        title: 'Thành công',
        description: 'Gửi gas thành công',
        status: 'success',
      });
    } catch (error) {
      console.log(error);

      let message = 'Có lỗi xảy ra khi gửi gas';
      if (error instanceof Error) {
        message = error.message;
      }
      if (isMetamaskError(error)) {
        message = error.data.message;
      }

      toast({
        title: 'Thất bại',
        description: message,
        status: 'error',
      });
    } finally {
      setIsSendingGas(false);
    }
  };

  const handleApproveManyUsers = async () => {
    setIsApprovingMany(true);
    try {
      if (!campaign?.groupId) {
        throw new Error('Missing campaign groupId');
      }
      const blockhashHash = campaign?.blockhashHash;
      const buyer = campaign?.creatorWalletAddress;

      if (!blockhashHash || !buyer) {
        throw new Error(
          'Thiếu blockhashHash hoặc buyer cho chiến dịch buy_tickets_auto. Vui lòng kiểm tra dữ liệu chiến dịch.',
        );
      }
      const walletAddresses = getWalletAddresses().filter(
        addr => addr !== undefined,
      ) as string[];
      if (!walletAddresses.length) {
        toast({
          title: 'Danh sách ví trống',
          description: 'Vui lòng chọn ít nhất một thành viên hợp lệ',
          status: 'warning',
        });
        return;
      }
      await chainManager.addManyWhitelisters({
        group: {
          groupId: campaign.groupId.toString(),
          blockhashHash,
          buyer,
        },
        users: walletAddresses,
      });

      await approveManyUsers({
        userIds: selectedUserIds,
        campaignId: campaign?._id as string,
      }).unwrap();

      toast({
        title: `Bạn đã duyệt thành công cho các thành viên chọn`,
        status: 'success',
      });
    } catch (err) {
      console.log(err);
      toast({
        title: `Duyệt thành viên thất bại`,
        status: 'error',
      });
    } finally {
      setIsApprovingMany(false);
    }
  };

  const getColumnsMembers = useMemo(() => {
    const commonColumns: Column<object>[] = [
      {
        Header: 'Trạng thái',
        accessor: 'status',
        id: 'status',
        filter: 'includes',
        Filter: SelectColumnFilter,
        Cell: props => {
          // eslint-disable-next-line react/prop-types
          const label: string = props.cell.value;
          const memberStatus = getMemberStatusByLabel(label);

          return (
            <Flex alignItems="center" gap={1}>
              <Tag color={memberStatus.color} bgColor={memberStatus.bgColor}>
                {label}
              </Tag>
              <Tooltip label={getInfoByStatus(memberStatus.status)}>
                <InfoOutlineIcon />
              </Tooltip>
            </Flex>
          );
        },
      },
      {
        Header: 'Người nhận',
        accessor: 'receiver',
        id: 'receiver',
        filter: 'text',
        Cell: props => {
          // eslint-disable-next-line react/prop-types
          const fullName = props.cell.value;
          return (
            <Text fontSize={16} color="darkBg">
              {isOwner ? (
                fullName ? (
                  fullName
                ) : (
                  <Tag colorScheme="red">Không tên</Tag>
                )
              ) : (
                <Tag>Đã ẩn</Tag>
              )}
            </Text>
          );
        },
      },
      {
        Header: 'Email',
        accessor: 'email',
        id: 'email',
        filter: (rows, ids, filterValue) => {
          const id = ids[0];
          return rows.filter(row => {
            const email = row.values[id];
            const members = campaign?.members ?? [];
            const member =
              members.find(mb => mb.user.email === email) ??
              ({} as CampaignMember);

            const isEmailVerified = !!member?.user?.isEmailVerified;
            if (filterValue === 'true') {
              return !!isEmailVerified === true;
            }
            if (filterValue === 'false') {
              return !!isEmailVerified === false;
            }

            return true;
          });
        },
        Filter: SelectEmailFilter,
        Cell: props => {
          // eslint-disable-next-line react/prop-types
          const email: string = props.cell.value;
          const members = campaign?.members ?? [];
          const member =
            members.find(mb => mb.user.email === email) ??
            ({} as CampaignMember);
          return (
            <Box fontSize={16} color="darkBg">
              {isOwner ? (
                <Flex alignItems="center" gap={1}>
                  <Text>
                    {email ? email : <Tag colorScheme="red">Không có</Tag>}
                  </Text>
                  {email &&
                    (member?.user?.isEmailVerified ? (
                      <StatusBadge
                        tooltip="Tài khoản đã xác thực"
                        status="trust"
                      />
                    ) : (
                      <StatusBadge
                        tooltip="Tài khoản chưa xác thực"
                        status="warning"
                      />
                    ))}
                </Flex>
              ) : (
                <Tag>Đã ẩn</Tag>
              )}
            </Box>
          );
        },
      },
      {
        Header: 'Địa chỉ ví',
        accessor: 'walletAddress',
        id: 'walletAddress',
        filter: 'text',
        Cell: props => {
          // eslint-disable-next-line react/prop-types
          const walletAddress: string = props.cell.value;
          return (
            <Flex alignItems="center" gap={1}>
              <Text fontSize={16} color="darkBg">
                {formatAddress(walletAddress)}
              </Text>
              <CopyIcon
                cursor="pointer"
                onClick={() => {
                  copyToClipboard(walletAddress);
                  toast({
                    title: 'Đã sao chép',
                    position: 'top-right',
                  });
                }}
              />
            </Flex>
          );
        },
      },
      {
        Header: 'Số dư (BNB)',
        accessor: 'balance',
        id: 'balance',
        filter: (rows, ids, filterValue) => {
          const id = ids[0];
          let [min, max] = filterValue;
          if (typeof min === 'string') {
            min = parseFloat(min);
          }
          if (typeof max === 'string') {
            max = parseFloat(max);
          }

          return rows.filter(row => {
            let balance = row.values[id];
            if (typeof balance === 'string') {
              balance = parseFloat(balance);
            }

            if (Number.isNaN(min) && Number.isNaN(max)) {
              return true;
            }

            if (Number.isNaN(min)) {
              return balance <= max;
            }

            if (Number.isNaN(max)) {
              return balance >= min;
            }

            return min <= balance && balance <= max;
          });
        },
        Filter: NumberRangeColumnFilter,
        Cell: props => {
          // eslint-disable-next-line react/prop-types
          const balance = props.cell.value;
          return (
            <VStack>
              <Text fontSize={16} color="darkBg">
                {balance}
              </Text>
            </VStack>
          );
        },
      },
    ];

    const commonDisableFilterProps = {
      disableFilters: true,
      disableGlobalFilter: true,
    };

    if (isOwner) {
      commonColumns.unshift({
        Header: (
          <VStack>
            <Text minW="10">Chọn tất cả</Text>
            <Checkbox
              color="primary"
              isChecked={isSelectAllUsers}
              onChange={() => {
                setIsSelectAllUsers(!isSelectAllUsers);
                handleSelectAllUsers(!isSelectAllUsers);
              }}
            />
          </VStack>
        ),
        accessor: 'cbUsers',
        id: 'cbUsiers',
        ...commonDisableFilterProps,
      });

      commonColumns.push({
        Header: '',
        accessor: 'actions',
        id: 'actions',
        ...commonDisableFilterProps,
      });
    }

    return commonColumns;
    return commonColumns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, isSelectAllUsers, campaign]);

  const getMemberData = useMemo<MemberRow[]>(() => {
    if (!campaign || !campaign.members) {
      return [];
    }
    return campaign.members.map(mb => {
      const status = mb.status.toString();
      const memberStatus = getMemberStatusProps(status);
      const memberUserId = mb.userId;
      const balance = round(
        parseFloat(fromWei(mb.user.balanceInWei || '0')),
        2,
      );

      return {
        cbUsers: (
          <Checkbox
            isChecked={
              memberUserId ? selectedUserIds.includes(memberUserId) : false
            }
            value={mb.userId}
            onChange={event => {
              updateSelectedWallets(event.target.value);
            }}
          />
        ),
        status: memberStatus.label,
        receiver: mb.user.fullName,
        email: mb.user.email,
        walletAddress: mb.user.walletAddress,
        balance,
        actions: ownerActions(
          status,
          mb.userId,
          campaign.groupId,
          mb.user.walletAddress,
        ),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaign,
    selectedUserIds,
    isOwner,
    eventName,
    isRemovingWhitelister,
    isClaimingLixi,
  ]);

  const isDisableApproveManyBtn =
    selectedUserIds.length === 0 ||
    selectedUserIds.some(id => {
      const members = campaign?.members || ([] as CampaignMember[]);
      const member = members.find(mb => mb.userId === id);
      return (
        member &&
        [CampaignMemberStatus.APPROVED, CampaignMemberStatus.CLAIMED].includes(
          member.status,
        )
      );
    });

  const ownerFooter = !isOwner ? null : (
    <Flex>
      <NotchedButton
        marginBottom="69px"
        bgColor="primary"
        color="white"
        w="20%"
        disabled={isDisableApproveManyBtn || isApprovingMany}
        isLoading={isApprovingMany}
        onClick={handleApproveManyUsers}
      >
        Duyệt lựa chọn
      </NotchedButton>
      <NotchedButton
        marginLeft="10px"
        disabled={selectedUserIds.length === 0 || isSendingGas}
        isLoading={isSendingGas}
        onClick={handleSendGasToMany}
      >
        Tặng gas
      </NotchedButton>
      <NotchedButton marginLeft="10px" onClick={onOpen}>
        Thêm người nhận
      </NotchedButton>
      <NotchedButton
        marginLeft="10px"
        onClick={exportCsv}
        isLoading={isExporting}
        disabled={isExporting}
      >
        Xuất CSV
      </NotchedButton>
      <AddReceiverModal
        campaignId={campaignId}
        isAdmin={isAdmin}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Flex>
  );

  return (
    <>
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore - suppress react-table generic explosion */}
      <Flex w="100%">
        <MembersTable
          columns={getColumnsMembers}
          data={getMemberData}
          hasFilter={isOwner}
        />
      </Flex>
      {ownerFooter}
    </>
  );
};
